import { put2 } from "@/shared/axios";
import useBranchId from "@/shared/hooks/useBranchId";
import { IUserSettings } from "@/shared/utils/interfaces";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useUserSettingsQuery from "../queries/user/useUserSettingsQuery";
import { DeliveryMethods } from "../utils/enum";
import { errorToast } from "../utils/helpers";
import useSourceHook from "./useSourceHook";

const deliveryMethods = [
  { label: "delivery", value: [DeliveryMethods.DELIVERY] },
  { label: "takeAway", value: [DeliveryMethods.TAKEAWAY] },
  { label: "posOrder", value: [DeliveryMethods.POS_ORDER] },
  {
    label: "deliveryByExternal",
    value: [DeliveryMethods.DELIVERY_BY_EXTERNAL],
  },
  {
    label: "qrTableOrder",
    value: [DeliveryMethods.QR_TABLE_ORDER],
  },
  {
    label: "dinein",
    value: [DeliveryMethods.DINE_IN],
  },
  {
    label: "takeout",
    value: [DeliveryMethods.TAKEOUT],
  },
];

const useUserSettingsHook = (customBranch?: number) => {
  const bId = useBranchId();
  const branch = customBranch ?? bId;
  const { t } = useTranslation();
  const query = useUserSettingsQuery();
  const { liveSources } = useSourceHook();

  useEffect(() => {
    if (query.isError) {
      const error = query.error as any;
      if (error?.response?.status === 404) {
        const message = t("orderTrackerSettingsNotFound");
        errorToast(message, "orderTrackerSettingsNotFound");
      }
    }
  }, [query.isError]);

  const putUserSettings = useMutation({
    mutationFn: (data: IUserSettings) => {
      return put2(`/internal/secure/pos/v2/user/settings/${branch}`, data);
    },
    onSuccess: async () => {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: async () => {
      errorToast();
    },
  });

  const showOnlyNewOrders = useMemo(
    () => query?.data?.show_only_new_orders,
    [query?.data]
  );
  const isOrderAcceptedAuto = useMemo(
    () => query?.data?.is_order_accepted_automatically,
    [query?.data]
  );
  const isPosOrderAcceptedAuto = useMemo(
    () => query?.data?.is_pos_order_accepted_automatically ?? true,
    [query?.data]
  );

  const isPosOrderPrintedAuto = useMemo(
    () => query?.data?.is_pos_order_printed_automatically ?? true,
    [query?.data]
  );

  const isOrderTrackerActive = useMemo(
    () => query?.data?.is_active,
    [query?.data]
  );
  const isOrderConfirmAsapMinButtonActive = useMemo(
    () => query?.data?.order_confirm_asap_minutes_buttons_is_active,
    [query?.data]
  );

  const stickyPosOrder = useMemo(
    () => query?.data?.sticky_pos_order_screen,
    [query?.data]
  );

  const lanes = useMemo(() => query?.data?.lanes, [query?.data]);

  const showOrderCount = useMemo(
    () => query?.data?.show_order_count,
    [query?.data]
  );

  return {
    query,
    putUserSettings,
    showOnlyNewOrders,
    isOrderAcceptedAuto,
    isPosOrderPrintedAuto,
    isOrderTrackerActive,
    isOrderConfirmAsapMinButtonActive,
    isPosOrderAcceptedAuto,
    stickyPosOrder,
    lanes,
    sources: liveSources,
    deliveryMethods,
    showOrderCount,
  };
};

export default useUserSettingsHook;
